import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const ParentHomework = () => {
  const routes = all_routes;

  // Mock data for children
  const [children] = useState([
    { id: "1", name: "Emma Johnson", grade: "10th", class: "10-A" },
    { id: "2", name: "James Johnson", grade: "8th", class: "8-B" },
  ]);

  const [selectedChildId, setSelectedChildId] = useState("1");
  const [activeTab, setActiveTab] = useState("due");

  const selectedChild = children.find((child) => child.id === selectedChildId);

  // Mock homework data
  const homeworkData = {
    due: [
      {
        id: "1",
        subject: "Mathematics",
        title: "Algebra Problems Chapter 5",
        description: "Complete exercises 1-20 from page 85",
        dueDate: "2024-08-06",
        dueTime: "Tomorrow",
        status: "pending",
        priority: "high",
        teacher: "Mrs. Smith",
      },
      {
        id: "2",
        subject: "English",
        title: "Essay on Shakespeare",
        description: "Write a 500-word essay on Hamlet",
        dueDate: "2024-08-07",
        dueTime: "2 days",
        status: "pending",
        priority: "medium",
        teacher: "Mr. Brown",
      },
      {
        id: "3",
        subject: "History",
        title: "World War II Research",
        description: "Research project on major battles",
        dueDate: "2024-08-08",
        dueTime: "3 days",
        status: "in-progress",
        priority: "medium",
        teacher: "Dr. Wilson",
      },
    ],
    overdue: [
      {
        id: "4",
        subject: "Science",
        title: "Lab Report - Chemical Reactions",
        description: "Complete lab report with observations",
        dueDate: "2024-08-03",
        dueTime: "2 days ago",
        status: "overdue",
        priority: "high",
        teacher: "Dr. Davis",
      },
      {
        id: "5",
        subject: "Art",
        title: "Portrait Drawing",
        description: "Submit pencil portrait drawing",
        dueDate: "2024-08-01",
        dueTime: "4 days ago",
        status: "overdue",
        priority: "low",
        teacher: "Ms. Garcia",
      },
    ],
    completed: [
      {
        id: "6",
        subject: "Mathematics",
        title: "Geometry Problems",
        description: "Triangle calculations",
        completedDate: "2024-08-02",
        status: "completed",
        grade: "A-",
        teacher: "Mrs. Smith",
      },
      {
        id: "7",
        subject: "English",
        title: "Poetry Analysis",
        description: "Analyze Robert Frost poems",
        completedDate: "2024-07-30",
        status: "completed",
        grade: "B+",
        teacher: "Mr. Brown",
      },
    ],
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "danger";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return "ti-check";
      case "in-progress":
        return "ti-clock";
      case "overdue":
        return "ti-alert-triangle";
      default:
        return "ti-circle";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "in-progress":
        return "primary";
      case "overdue":
        return "danger";
      default:
        return "warning";
    }
  };

  const renderHomeworkCard = (homework: any) => (
    <div key={homework.id} className="card mb-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <div
              className={`avatar avatar-sm bg-${getStatusColor(
                homework.status
              )}-transparent me-3`}
            >
              <i
                className={`ti ${getStatusIcon(
                  homework.status
                )} text-${getStatusColor(homework.status)}`}
              ></i>
            </div>
            <div>
              <h6 className="mb-1">{homework.title}</h6>
              <p className="text-muted mb-0">
                <i className="ti ti-book me-1"></i>
                {homework.subject} • {homework.teacher}
              </p>
            </div>
          </div>
          {homework.priority && (
            <span className={`badge bg-${getPriorityColor(homework.priority)}`}>
              {homework.priority}
            </span>
          )}
        </div>

        <p className="text-muted mb-3">{homework.description}</p>

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className="ti ti-calendar me-2 text-muted"></i>
            <span
              className={`text-${
                homework.status === "overdue" ? "danger" : "muted"
              }`}
            >
              {homework.status === "completed"
                ? `Completed: ${homework.completedDate}`
                : `Due: ${homework.dueTime}`}
            </span>
          </div>
          {homework.grade && (
            <span className="badge bg-success">{homework.grade}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header with Child Selector */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Homework Overview</h3>
              <p className="text-muted mb-0">
                Track your child's assignments and progress
              </p>
            </div>
            <div className="col-auto">
              {children.length > 1 && (
                <div className="dropdown">
                  <button
                    className="btn btn-outline-primary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-user me-2"></i>
                    {selectedChild?.name}
                  </button>
                  <ul className="dropdown-menu">
                    {children.map((child) => (
                      <li key={child.id}>
                        <button
                          className={`dropdown-item ${
                            selectedChildId === child.id ? "active" : ""
                          }`}
                          onClick={() => setSelectedChildId(child.id)}
                        >
                          {child.name} - {child.grade} {child.class}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-warning mb-2">
                    <i className="ti ti-clock text-white fs-2"></i>
                  </div>
                  <h4 className="text-warning">{homeworkData.due.length}</h4>
                  <p className="mb-0">Due Soon</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-danger-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-danger mb-2">
                    <i className="ti ti-alert-triangle text-white fs-2"></i>
                  </div>
                  <h4 className="text-danger">{homeworkData.overdue.length}</h4>
                  <p className="mb-0">Overdue</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success mb-2">
                    <i className="ti ti-check text-white fs-2"></i>
                  </div>
                  <h4 className="text-success">
                    {homeworkData.completed.length}
                  </h4>
                  <p className="mb-0">Completed</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-primary mb-2">
                    <i className="ti ti-percentage text-white fs-2"></i>
                  </div>
                  <h4 className="text-primary">85%</h4>
                  <p className="mb-0">On Time Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "due" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("due")}
                  >
                    Due ({homeworkData.due.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "overdue" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("overdue")}
                  >
                    Overdue ({homeworkData.overdue.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "completed" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("completed")}
                  >
                    Completed ({homeworkData.completed.length})
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {/* Due Homework */}
              {activeTab === "due" && (
                <div>
                  {homeworkData.due.length > 0 ? (
                    homeworkData.due.map((homework) =>
                      renderHomeworkCard(homework)
                    )
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-success-transparent mb-3">
                        <i className="ti ti-check fs-2 text-success"></i>
                      </div>
                      <h5>No pending homework</h5>
                      <p className="text-muted">
                        All assignments are up to date!
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Overdue Homework */}
              {activeTab === "overdue" && (
                <div>
                  {homeworkData.overdue.length > 0 ? (
                    homeworkData.overdue.map((homework) =>
                      renderHomeworkCard(homework)
                    )
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-success-transparent mb-3">
                        <i className="ti ti-check fs-2 text-success"></i>
                      </div>
                      <h5>No overdue homework</h5>
                      <p className="text-muted">Great job staying on track!</p>
                    </div>
                  )}
                </div>
              )}

              {/* Completed Homework */}
              {activeTab === "completed" && (
                <div>
                  {homeworkData.completed.length > 0 ? (
                    homeworkData.completed.map((homework) =>
                      renderHomeworkCard(homework)
                    )
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-info-transparent mb-3">
                        <i className="ti ti-book fs-2 text-info"></i>
                      </div>
                      <h5>No completed homework</h5>
                      <p className="text-muted">
                        Completed assignments will appear here.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParentHomework;
